import { readConfig } from "./config";
import type { ApiClient } from "./api";
import type { AdminClient, AdminUser } from "./outlet-context";

const config = readConfig();
const clientPortalUrl = config.ok ? config.clientPortalUrl : "";

/** Whether the act-as launch is possible for this client right now. */
export function canOpenClientPortal(client: AdminClient | undefined, api: ApiClient | null): boolean {
  return Boolean(clientPortalUrl && client?.d1_database_id && api?.getToken());
}

/** Tooltip explaining the button state (why it's disabled, or what it does). */
export function openClientPortalTitle(client: AdminClient | undefined): string {
  if (!clientPortalUrl) return "Set PUBLIC_CLIENT_PORTAL_URL to enable";
  if (!client?.d1_database_id) return "Client site is not provisioned yet";
  return "Open this client's portal to manage their site";
}

/**
 * Open the Client Portal in an act-as session for this client. The API mints a
 * tenant-scoped act-as token (the admin's own Supabase JWT never leaves this
 * origin), handed over via the URL hash so it is never sent to a server.
 * `path` lands the session on a specific portal page (default: the Dashboard) —
 * the portal keeps the pathname when it consumes the hash.
 *
 * The tab is opened synchronously inside the click before the token request
 * resolves — popup blockers kill any window.open that runs after an await
 * (see docs/gotchas.md).
 */
export async function openClientPortal(
  client: AdminClient,
  api: ApiClient | null,
  user: AdminUser | null,
  path = "/"
): Promise<void> {
  if (!canOpenClientPortal(client, api)) return;
  const tab = window.open("", "_blank");
  try {
    const { token } = await api!.request<{ token: string }>("/admin/act-as-token", {
      method: "POST",
      body: JSON.stringify({ tenantId: client.id }),
    });
    const url = new URL(clientPortalUrl);
    if (path !== "/") url.pathname = path;
    url.hash = new URLSearchParams({
      act_as: client.id,
      act_as_token: token,
      admin_email: user?.email ?? "",
      label: client.business_name ?? client.id,
      return_to: window.location.origin,
    }).toString();
    if (tab) {
      tab.location.replace(url.toString());
    } else {
      window.open(url.toString(), "_blank");
    }
  } catch (err) {
    tab?.close();
    window.alert(
      `Could not open the client portal: ${err instanceof Error ? err.message : err}`
    );
  }
}
